/**
 * AdminUserList Component (AC-921, AC-1098)
 *
 * Displays list of users with admin status, promote/demote actions,
 * and delete user with a single-step confirmation modal.
 * Prevents self-demotion and self-deletion.
 * Cannot delete admin users (must demote first).
 *
 * Epic: AC-915 - Soft-Launch Email Allowlist with Admin Portal
 */

import React, { useState } from 'react';
import { Search, ShieldCheck, ShieldOff, Trash2, AlertTriangle } from 'lucide-react';
import { AdminUserSummary } from '../../types';
import CustomSelect from '../common/CustomSelect';

export type AdminFilterValue = 'all' | 'admin' | 'non-admin';

interface AdminUserListProps {
  users: AdminUserSummary[];
  total: number;
  isLoading: boolean;
  currentUserId: number;
  onRoleChange: (userId: number, isAdmin: boolean) => Promise<void>;
  onDeleteUser: (userId: number) => Promise<void>;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  adminFilter: AdminFilterValue;
  onAdminFilterChange: (filter: AdminFilterValue) => void;
}

const AdminUserList: React.FC<AdminUserListProps> = ({
  users,
  total,
  isLoading,
  currentUserId,
  onRoleChange,
  onDeleteUser,
  searchQuery,
  onSearchChange,
  adminFilter,
  onAdminFilterChange,
}) => {
  const [deleteTarget, setDeleteTarget] = useState<AdminUserSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteClick = (user: AdminUserSummary) => {
    setDeleteTarget(user);
    setDeleteError(null);
  };

  const handleCancelDelete = () => {
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await onDeleteUser(deleteTarget.user_id);
      setDeleteTarget(null);
    } catch (error: any) {
      setDeleteError(error?.response?.data?.detail || error?.message || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading && users.length === 0) {
    return (
      <div data-testid="users-loading" aria-busy="true" className="animate-pulse space-y-3">
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
      </div>
    );
  }

  return (
    <div>
      {/* Search and Filter */}
      <div className="mb-4 flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            role="textbox"
            aria-label="Search users"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <CustomSelect
          aria-label="Filter by role"
          value={adminFilter}
          onChange={(value) => onAdminFilterChange(value as AdminFilterValue)}
          options={[
            { value: 'all', label: 'All Users' },
            { value: 'admin', label: 'Admins Only' },
            { value: 'non-admin', label: 'Regular Users' },
          ]}
        />
      </div>

      {/* User list or empty state */}
      {users.length === 0 ? (
        <div data-testid="users-empty" className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No users found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {users.map((user) => {
            const isCurrentUser = user.user_id === currentUserId;

            return (
              <div
                key={user.user_id}
                data-current-user={isCurrentUser ? 'true' : undefined}
                className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-warm-white">
                      {user.username}
                    </span>
                    {isCurrentUser && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">(You)</span>
                    )}
                    {user.is_admin && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">
                        Admin
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                </div>

                <div className="flex items-center gap-2">
                  {user.is_admin && !isCurrentUser && (
                    <button
                      onClick={() => onRoleChange(user.user_id, false)}
                      className="btn text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1"
                      aria-label="Demote"
                    >
                      <ShieldOff className="w-3.5 h-3.5" />
                      Demote
                    </button>
                  )}
                  {!user.is_admin && (
                    <>
                      <button
                        onClick={() => onRoleChange(user.user_id, true)}
                        className="btn text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-1"
                        aria-label="Promote"
                      >
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Promote
                      </button>
                      {!isCurrentUser && (
                        <button
                          onClick={() => handleDeleteClick(user)}
                          className="btn text-sm px-3 py-1.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-1"
                          aria-label={`Delete user ${user.username}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">
                Delete User
              </h3>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              This will permanently delete <span className="font-semibold">{deleteTarget.username}</span> and
              all their data including projects, agents, workflows, run history, and MCP servers.
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-4">
              This action cannot be undone.
            </p>

            {deleteError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">{deleteError}</p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                disabled={isDeleting}
                className="btn px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                autoFocus
                className="btn px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserList;
